/**
 * Same selector list as `trapFocus` from `@studiometa/js-toolkit/utils`.
 */
const FOCUSABLE_ELEMENTS = [
  'a[href]:not([tabindex^="-"]):not([inert])',
  'area[href]:not([tabindex^="-"]):not([inert])',
  'input:not([disabled]):not([inert])',
  'select:not([disabled]):not([inert])',
  'textarea:not([disabled]):not([inert])',
  'button:not([disabled]):not([inert])',
  'iframe:not([tabindex^="-"]):not([inert])',
  'audio:not([tabindex^="-"]):not([inert])',
  'video:not([tabindex^="-"]):not([inert])',
  '[contenteditable]:not([tabindex^="-"]):not([inert])',
  '[tabindex]:not([tabindex^="-"]):not([inert])',
].join(', ');

/**
 * Keep Tab / Shift+Tab focus cycling inside `element`.
 *
 * Drop-in replacement for js-toolkit's `trapFocus`, which also counts
 * elements that are not rendered (`display: none` at the current
 * breakpoint, closed nested submenus, `visibility: hidden` panels...),
 * although native Tab skips them. When the first or last
 * candidate is one of those, calling `focus()` on it is a no-op while the
 * native Tab is still prevented (focus gets stuck), or the wrap-around is
 * never triggered (focus escapes the container).
 * @param {HTMLElement} element
 * @param {KeyboardEvent} event
 */
export function trapFocus(element, event) {
  if (event.key !== 'Tab') {
    return;
  }

  // No client rects: `display: none` (itself or an ancestor). `visibility`
  // is inherited, so the element's own computed value also covers a hidden
  // ancestor (e.g. the closed search panel). Both are skipped by native Tab.
  const focusableChildren = Array.from(element.querySelectorAll(FOCUSABLE_ELEMENTS)).filter(
    (el) =>
      el.getClientRects().length > 0 &&
      getComputedStyle(el).visibility === 'visible' &&
      !el.closest('[inert]'),
  );

  if (!focusableChildren.length) {
    return;
  }

  const first = focusableChildren[0];
  const last = focusableChildren.at(-1);
  const focusedItemIndex = focusableChildren.indexOf(document.activeElement);

  if (focusedItemIndex < 0) {
    event.preventDefault();
    (event.shiftKey ? last : first).focus();
  } else if (event.shiftKey && focusedItemIndex === 0) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && focusedItemIndex === focusableChildren.length - 1) {
    event.preventDefault();
    first.focus();
  }
}
